import { useEffect, useState } from "react";
import { useOfflineSync } from "@/lib/offline-sync";

/**
 * The app-wide signal banner.
 *
 * #1505 — two things were wrong with it in the field. It sat at `top-0` with no
 * safe-area inset, so on a phone with a tall status bar (Dynamic Island, or an
 * in-progress call, which makes the bar taller still) the text was rammed under
 * the status bar and unreadable: the tech was being told the cause of his
 * problem in words he physically could not see. And it only ever announced a
 * problem — never what the app was doing about it, and never offered a way to
 * push. It now clears the status bar, says the reassuring thing (the work is
 * safe on the phone and sends itself), and carries a retry the tech can tap
 * from any screen.
 */
export function OfflineIndicator() {
    // Reachability-verified, not raw `navigator.onLine`: the flag's
    // false-negatives made this banner shout "You're offline" with full
    // signal. See `connectivity.ts`.
    const { isOnline: online, pendingCount: queueCount, sync, syncing } = useOfflineSync();
    const [retrying, setRetrying] = useState(false);

    // Drop the "sending" state once the queue actually clears, so the banner
    // doesn't hang on a spinner after a successful drain.
    useEffect(() => {
        if (queueCount === 0) setRetrying(false);
    }, [queueCount]);

    const sending = syncing || retrying;
    if (online && !sending && queueCount === 0) return null;

    return (
        <div
            role="status"
            aria-live="polite"
            data-testid="offline-indicator"
            className={`fixed top-0 inset-x-0 z-[9999] flex items-center justify-center gap-2 px-4 pb-2 text-sm font-medium ${
                sending ? "bg-primary text-primary-foreground" : "bg-warning text-warning-foreground"
            }`}
            // The status bar is the OS's, not ours. `env(safe-area-inset-top)`
            // is the only honest measure of it — it grows for the Dynamic
            // Island and again for the green in-call bar — and it needs
            // `viewport-fit=cover` on the viewport meta (client/index.html) to
            // report anything but 0. Tailwind can't express the calc, so it
            // lives here rather than as an approximate hard-coded pt-*.
            style={{ paddingTop: "calc(0.5rem + env(safe-area-inset-top, 0px))" }}
        >
            {sending ? (
                <>
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                    </svg>
                    Sending {queueCount} saved {queueCount === 1 ? "change" : "changes"}…
                </>
            ) : (
                <>
                    <svg
                        className="h-4 w-4 shrink-0"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M18.364 5.636a9 9 0 010 12.728M5.636 18.364a9 9 0 010-12.728M8.464 15.536a5 5 0 010-7.072M15.536 8.464a5 5 0 010 7.072"
                        />
                        <line x1="4" y1="4" x2="20" y2="20" strokeLinecap="round" />
                    </svg>
                    <span>
                        {queueCount > 0
                            ? `No signal — ${queueCount} ${queueCount === 1 ? "change is" : "changes are"} saved on this phone and will send themselves.`
                            : "No signal — your work is saved on this phone. Keep going."}
                    </span>
                    {queueCount > 0 && (
                        <button
                            type="button"
                            data-testid="offline-indicator-retry"
                            className="shrink-0 rounded-full bg-black/15 px-2.5 py-0.5 text-xs font-semibold"
                            onClick={async () => {
                                setRetrying(true);
                                try {
                                    await sync({ force: true });
                                } finally {
                                    setRetrying(false);
                                }
                            }}
                        >
                            Try now
                        </button>
                    )}
                </>
            )}
        </div>
    );
}
